/**
 * Human labels for durable background jobs (job_run_state). Unknown job
 * names fall back to the raw `job:<name>`.
 */
import type { AdminJobRunState } from '@sill/types/api';
import type { PillTone } from '../../components/atoms/Pill.js';

export const JOB_LABELS: Readonly<Record<string, { label: string; what: string }>> = {
  'job:operational_retention': {
    label: 'Operational data purge',
    what: 'Proposes (manual) or runs (automatic) the retention purge of logs and telemetry past their windows.',
  },
  'job:account_deletion_grace': {
    label: 'Account deletion grace',
    what: 'Proposes (manual) or completes (automatic) deletion of accounts whose 30-day grace has ended.',
  },
  'job:account_lifecycle': {
    label: 'Inactive account lifecycle',
    what: 'Warns inactive accounts, then starts deletion. Off unless enabled in Settings.',
  },
  'job:edge_kv_reconcile': {
    label: 'Edge cache cleanup',
    what: 'Removes edge entries for deleted sites.',
  },
  'job:buyer_detail_retention': {
    label: 'Buyer detail retention',
    what: "Purges decryptable buyer data past each account's retention setting.",
  },
  'job:acp_spt_retention': {
    label: 'ACP token retention',
    what: 'Clears expired shared payment tokens (ACP is dormant).',
  },
  'job:shadow_decision_expiry': {
    label: 'Shadow decision expiry',
    what: 'Deletes expired shadow-mode decisions.',
  },
  'job:stripe_spt_correlation_sweep': {
    label: 'Stripe SPT correlation sweep',
    what: 'Alerts on stuck correlations and deletes old finished ones.',
  },
  'job:magic_link_sweeper': {
    label: 'Sign-in link cleanup',
    what: 'Deletes expired magic links and sessions.',
  },
  'job:agent_registry_link_sweep': {
    label: 'Agent registry link sweep',
    what: 'Links observed bots to the registry (backfill + re-link after changes) and marks rotation spoof clusters.',
  },
  'job:agent_registry_discovery': {
    label: 'Agent registry discovery',
    what: 'Weekly: proposes unregistered bots, spoof clusters and dormant agents for review on Agent registry.',
  },
  'job:agent_registry_auto': {
    label: 'Agent registry automatic',
    what: 'Daily: refreshes candidates, records what Automatic would approve and, when Automatic is on, approves up to 50 a day.',
  },
  'job:operator_ip_ranges_refresh': {
    label: 'Operator IP lists',
    what: 'Daily: fetches the IP lists bot operators publish and stores them; a failed fetch keeps the last good list.',
  },
  'job:agent_logo_refresh': {
    label: 'Agent logos',
    what: 'Every 6 hours: fetches up to 10 bot operators’ site icons (PNG, ICO, WebP or JPEG; never SVG) for the dashboard; a failed fetch keeps the last good icon and retries after 7 days.',
  },
  'job:agent_observed_reclassify': {
    label: 'Bot reclassify',
    what: "Refreshes bot summaries whose registry status improved.",
  },
  'job:metrics_rollup': {
    label: 'Metrics rollup',
    what: 'Recomputes the last three days of the Metrics dashboard.',
  },
  'job:agent_behaviour_detect': {
    label: 'Agent behaviour drift',
    what: 'Compares each verified agent’s last 24 hours to its 30-day baseline on this site and writes a threat alert when something has changed.',
  },
  'job:billing_reconcile': {
    label: 'Billing reconcile',
    what: 'Re-reads each live Stripe subscription and repairs drift from missed webhooks.',
  },
  'job:site_rescan_schedule': {
    label: 'Scheduled site rescans',
    what: "Re-runs the site scan for sites whose re-scan schedule is due.",
  },
  'job:skill_endpoint_recheck': {
    label: 'Skill endpoint recheck',
    what: 'Re-probes merchant skill endpoints that have been marked unreachable; promotes recovered ones back to healthy and republishes.',
  },
  'job:geo_db_refresh': {
    label: 'Geo DB refresh',
    what: 'Daily: refreshes the geo waterfall. MaxMind GeoLite2 City + ASN (20 h skip, requires MAXMIND_LICENSE_KEY) and DB-IP Lite City + ASN (25 d skip, no key, monthly file). The only path that downloads geo data; readers hydrate from geo_db_cache. On HTTP 429 (MaxMind quota) keeps the previous row and waits a full day.',
  },
  'job:cloud_ranges_refresh': {
    label: 'Cloud IP ranges',
    what: 'Daily: refreshes the authoritative cloud / CDN IP ranges table (GCP, AWS, Oracle, DigitalOcean, Cloudflare). A match in cloud_ip_range marks a sender as a hosting network even when the ASN reader was silent. Safety gates (shrink_half, grow_double, floor) protect against a poisoned mirror.',
  },
  'job:tier_l_mine': {
    label: 'Tier L miner (shadow)',
    what: 'ADR-0249 S1. Daily: aggregates attested-tier threat_signal rows into cross_tenant_path_counter (no tenant columns), scores path candidates against the ADR §4.3 formula, runs §4.4 HARD disqualifiers, and (if the AI judge flag is on) calls Haiku for a veto-only verdict. Writes tier_l_candidate rows for Sill-ops inspection; nothing lands in any Worker envelope.',
  },
};

export function jobLabel(job_name: string): string {
  return JOB_LABELS[job_name]?.label ?? job_name;
}

export type JobHealth = 'ok' | 'failed' | 'overdue' | 'running' | 'never';

export function jobHealth(j: AdminJobRunState): JobHealth {
  if (j.last_completed_at === null) return j.last_started_at === null ? 'never' : 'running';
  if (j.overdue) return 'overdue';
  return j.last_status === 'failed' ? 'failed' : 'ok';
}

export function jobHealthTone(h: JobHealth): PillTone {
  switch (h) {
    case 'ok':
      return 'lime';
    case 'failed':
      return 'coral';
    case 'overdue':
      return 'amber';
    case 'running':
      return 'sky';
    case 'never':
      return 'neutral';
  }
}

export function formatEvery(seconds: number): string {
  if (seconds % 86_400 === 0) return seconds === 86_400 ? 'daily' : `every ${seconds / 86_400} days`;
  if (seconds % 3_600 === 0) return seconds === 3_600 ? 'hourly' : `every ${seconds / 3_600} h`;
  return `every ${Math.round(seconds / 60)} min`;
}

/** Compact `key: value` rendering of a run summary. */
export function formatSummary(summary: AdminJobRunState['last_summary']): string {
  if (summary === null) return '—';
  const parts = Object.entries(summary)
    .filter(([, v]) => v !== null)
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${String(v)}`);
  return parts.length === 0 ? '—' : parts.join(' · ');
}
